import api from '../api';
import { compressImageForNote, NoteMediaQuotaError, NOTES_MEDIA_STORAGE_QUOTA_CODE } from '@trove-shared';
import { getAxiosErrorResponseData } from '@trove/api-client';
import { assertNoteMediaUploadAllowed } from '@/utils/noteMediaQuota';
import type { MediaUsageKind, NoteMediaListItem, NoteMediaListResponse } from '@trove/api-client';

type SignResponse = {
  status: string;
  code?: string;
  uploadUrl?: string;
  publicUrl?: string;
  mediaId?: string;
  uploadViaApi?: boolean;
  uploadHeaders?: Record<string, string>;
  message?: string;
};

function throwNoteMediaApiError(err: unknown, fallback: string): never {
  const data = getAxiosErrorResponseData(err) as { code?: string; message?: string } | undefined;
  if (data?.code === NOTES_MEDIA_STORAGE_QUOTA_CODE) {
    throw new NoteMediaQuotaError(
      data.message ?? 'Note image storage quota exceeded. Remove images or upgrade your plan.'
    );
  }
  if (data?.message) throw new Error(data.message);
  if (err instanceof Error && err.message) throw err;
  throw new Error(fallback);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const view = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < view.length; i += 1) {
    binary += String.fromCharCode(view[i]!);
  }
  return btoa(binary);
}

export async function uploadNoteMediaFile(
  file: Blob,
  opts?: { source?: 'screenshot' | 'upload' | 'paste' }
): Promise<{ publicUrl: string; mediaId: string }> {
  const compressed = await compressImageForNote(file);
  await assertNoteMediaUploadAllowed(compressed.byteLength);
  let signRes;
  try {
    signRes = await api.post<SignResponse>('/notes/media/sign', {
      contentType: compressed.contentType,
      byteLength: compressed.byteLength,
      staging: true,
      ...(opts?.source ? { source: opts.source } : {}),
    });
  } catch (err) {
    throwNoteMediaApiError(err, 'Failed to sign upload');
  }
  if (
    signRes.data.status !== 'success' ||
    !signRes.data.publicUrl ||
    !signRes.data.mediaId
  ) {
    if (signRes.data.code === NOTES_MEDIA_STORAGE_QUOTA_CODE) {
      throw new NoteMediaQuotaError(
        signRes.data.message ?? 'Note image storage quota exceeded. Remove images or upgrade your plan.'
      );
    }
    throw new Error(signRes.data.message ?? 'Failed to sign upload');
  }
  const uploadViaApi =
    signRes.data.uploadViaApi === true ||
    (!signRes.data.uploadUrl &&
      typeof signRes.data.publicUrl === 'string' &&
      /(?:127\.0\.0\.1|localhost):9199\//.test(signRes.data.publicUrl));
  if (!uploadViaApi && !signRes.data.uploadUrl) {
    throw new Error(signRes.data.message ?? 'Failed to sign upload');
  }
  if (uploadViaApi) {
    let uploadRes;
    try {
      uploadRes = await api.post<{ status: string; message?: string }>('/notes/media/upload', {
        mediaId: signRes.data.mediaId,
        contentType: compressed.contentType,
        byteLength: compressed.byteLength,
        data: await blobToBase64(compressed.blob),
      });
    } catch (err) {
      throwNoteMediaApiError(err, 'Upload failed');
    }
    if (uploadRes.data.status !== 'success') {
      throw new Error(uploadRes.data.message ?? 'Upload failed');
    }
  } else {
    const putRes = await fetch(signRes.data.uploadUrl!, {
      method: 'PUT',
      headers: {
        'Content-Type': compressed.contentType,
        ...(signRes.data.uploadHeaders ?? {}),
      },
      body: compressed.blob,
    });
    if (!putRes.ok) {
      throw new Error(`Upload failed (${putRes.status})`);
    }
  }
  return { publicUrl: signRes.data.publicUrl, mediaId: signRes.data.mediaId };
}

export type { NoteMediaListItem, MediaUsageKind };

export type ListNoteMediaParams = {
  cursor?: string;
  limit?: number;
  sid?: string;
  status?: 'all' | 'inUse' | 'orphan';
  usageKind?: MediaUsageKind;
};

export async function listNoteMedia(params: ListNoteMediaParams = {}): Promise<NoteMediaListResponse> {
  const res = await api.get<NoteMediaListResponse>('/notes/media', { params });
  if (res.data.status !== 'success') {
    throw new Error('Failed to load storage');
  }
  return res.data;
}

export async function deleteOrphanNoteMedia(mediaId: string): Promise<void> {
  const res = await api.delete<{ status: string; message?: string }>(`/notes/media/${encodeURIComponent(mediaId)}`);
  if (res.data.status !== 'success') {
    throw new Error(res.data.message ?? 'Delete failed');
  }
}

const IMAGE_UPLOAD_EXT = /\.(jpe?g|png|webp|gif|bmp|heic|heif|avif)$/i;

/** Accept image/* MIME or common image extensions (some browsers omit file.type). */
export function isLikelyImageUploadFile(file: File | Blob & { name?: string }): boolean {
  if (file.type?.startsWith('image/')) return true;
  const name = 'name' in file && typeof file.name === 'string' ? file.name : '';
  return IMAGE_UPLOAD_EXT.test(name);
}
